/**
 * Content Schema JSON to Lexical JSON Converter
 *
 * Converts editor-agnostic Content Schema payloads to Lexical editor state JSON.
 */

import type {
  ActionItemNode,
  ChecklistNode,
  CodeNode,
  ContentNode,
  DecisionNode,
  HeadingNode,
  ImageNode,
  LinkNode,
  ListItemNode,
  ListNode,
  ParagraphNode,
  PriceTrendNode,
  QuoteNode,
  RootNode,
  SelfContractNode,
  StockNode,
  TagNode,
  TextNode,
} from "@hamecho/content-schema";

/**
 * Converts canonical Content Schema JSON to Lexical JSON.
 */
export function convertContentSchemaToLexical(contentSchemaJSON: string): string {
  let parsed: unknown;

  try {
    parsed = JSON.parse(contentSchemaJSON);
  } catch (error) {
    throw new Error(`Failed to parse Content Schema JSON: ${error}`);
  }

  const root = asRootNode(parsed);
  const lexicalRoot = {
    root: {
      type: "root",
      version: 1,
      direction: root.direction === "null" ? null : (root.direction ?? "ltr"),
      children: root.children.map((child) => convertNodeToLexical(child)).filter(Boolean),
    },
  };

  return JSON.stringify(lexicalRoot);
}

function asRootNode(value: unknown): RootNode {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid Content Schema JSON: expected root object");
  }

  const root = value as Record<string, unknown>;
  if (root.type !== "root") {
    throw new Error("Invalid Content Schema JSON: root.type must be 'root'");
  }
  if (root.version !== 1) {
    throw new Error("Invalid Content Schema JSON: root.version must be 1");
  }
  if (typeof root.key !== "string") {
    throw new Error("Invalid Content Schema JSON: root.key must be string");
  }
  if (!Array.isArray(root.children)) {
    throw new Error("Invalid Content Schema JSON: root.children must be an array");
  }

  return root as unknown as RootNode;
}

function isContentNode(value: unknown): value is ContentNode {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.type === "string" && typeof candidate.version === "number" && typeof candidate.key === "string";
}

function toContentChildren(children: unknown[]): ContentNode[] {
  return children.filter(isContentNode);
}

/**
 * Converts a Content Schema node to Lexical node.
 */
function convertNodeToLexical(contentNode: ContentNode): Record<string, unknown> {
  const type = contentNode.type;
  const version = contentNode.version;
  const key = contentNode.key;

  switch (type) {
    case "paragraph":
      return convertParagraphToLexical(contentNode, version, key);
    case "text":
      return convertTextToLexical(contentNode, version, key);
    case "link":
      return convertLinkToLexical(contentNode, version, key);
    case "linebreak":
      return convertLinebreakToLexical(version, key);
    case "image":
      return convertImageToLexical(contentNode, version, key);
    case "stock":
      return convertStockToLexical(contentNode, version, key);
    case "tag":
      return convertTagToLexical(contentNode, version, key);
    case "decision":
      return convertDecisionToLexical(contentNode, version, key);
    case "self-contract":
      return convertSelfContractToLexical(contentNode, version, key);
    case "checklist":
      return convertChecklistToLexical(contentNode, version, key);
    case "price-trend":
      return convertPriceTrendToLexical(contentNode, version, key);
    case "action-item":
      return convertActionItemToLexical(contentNode, version, key);
    case "heading":
      return convertHeadingToLexical(contentNode, version, key);
    case "list":
      return convertListToLexical(contentNode, version, key);
    case "listitem":
      return convertListItemToLexical(contentNode, version, key);
    case "quote":
      return convertQuoteToLexical(contentNode, version, key);
    case "code":
      return convertCodeToLexical(contentNode, version, key);
    default:
      console.warn(`[content-schema] reject unknown content-schema node type: ${type}`);
      throw new Error(`Unknown content-schema node type: ${type}`);
  }
}

function convertParagraphToLexical(node: ParagraphNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "paragraph",
    version,
    key,
    format: node.format ?? "",
    indent: node.indent ?? 0,
    direction: node.direction === "null" ? null : (node.direction ?? null),
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertTextToLexical(node: TextNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "text",
    version,
    key,
    text: node.text || "",
    style: node.style,
    mode: node.mode,
    format: node.format,
    detail: node.detail,
  };
}

function convertLinkToLexical(node: LinkNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "link",
    version,
    key,
    url: node.url || "",
    title: node.title,
    target: node.target,
    rel: node.rel,
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertLinebreakToLexical(version: number, key: string): Record<string, unknown> {
  return {
    type: "linebreak",
    version,
    key,
  };
}

function convertImageToLexical(node: ImageNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "image",
    version,
    key,
    src: node.src || "",
    altText: node.altText,
    width: node.width,
    height: node.height,
  };
}

function convertStockToLexical(node: StockNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "stock",
    version,
    key,
    symbol: node.symbol || "",
    sentiment: node.sentiment || "neutral",
    confidence: node.confidence ?? null,
  };
}

function convertTagToLexical(node: TagNode, version: number, key: string): Record<string, unknown> {
  const tagId = node.tagId;
  if (!tagId) {
    throw new Error("TagNode must have tagId");
  }
  return {
    type: "tag",
    version,
    key,
    tagId,
  };
}

function convertDecisionToLexical(node: DecisionNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "decision",
    version,
    key,
    ticker: node.ticker || "",
    contractId: node.contractId,
    decision: node.decision || "HOLD",
    actionType: node.actionType || "NONE",
    price: node.price ?? 0,
    quantity: node.quantity ?? 0,
    reason: node.reason || "",
    executedAt: node.executedAt || new Date().toISOString(),
    createdAt: node.createdAt,
    confidence: node.confidence,
    riskLevel: node.riskLevel,
    timeHorizon: node.timeHorizon,
  };
}

function convertSelfContractToLexical(node: SelfContractNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "self-contract",
    version,
    key,
    ticker: node.ticker || "",
    template: node.template || "value-oriented",
    expiration: node.expiration || "",
    idea: node.idea || "",
    triggerType: node.trigger.type || "price",
    triggerPriceLevel: node.trigger.priceLevel,
    triggerOperator: node.trigger.operator,
    triggerNotes: node.trigger.notes || "",
    action: node.action || "",
    status: node.status || "active",
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
  };
}

function convertChecklistToLexical(node: ChecklistNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "checklist",
    version,
    key,
    items: Array.isArray(node.items) ? node.items : [],
    title: node.title,
    templateId: node.templateId,
  };
}

function convertPriceTrendToLexical(node: PriceTrendNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "price-trend",
    version,
    key,
    symbol: node.symbol || "",
    trend: node.trend || "NEUTRAL",
    support: node.support ?? 0,
    resistance: node.resistance ?? 0,
    target: node.target ?? 0,
    stopLoss: node.stopLoss,
    notes: node.notes,
    chartUrl: node.chartUrl,
  };
}

function convertActionItemToLexical(node: ActionItemNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "action-item",
    version,
    key,
    action: node.action || "",
    priority: node.priority || "medium",
    status: node.status || "todo",
    dueDate: node.dueDate,
    assignee: node.assignee,
    notes: node.notes,
  };
}

function convertHeadingToLexical(node: HeadingNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "heading",
    version,
    key,
    tag: node.tag || "h1",
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertListToLexical(node: ListNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "list",
    version,
    key,
    listType: node.listType || "bullet",
    start: node.start,
    indent: 0, // Lexical requires indent, default to 0 for Content Schema
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertListItemToLexical(node: ListItemNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "listitem",
    version,
    key,
    checked: node.checked,
    indent: 0, // Lexical requires indent, default to 0 for Content Schema
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertQuoteToLexical(node: QuoteNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "quote",
    version,
    key,
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}

function convertCodeToLexical(node: CodeNode, version: number, key: string): Record<string, unknown> {
  return {
    type: "code",
    version,
    key,
    language: node.language,
    children: toContentChildren(node.children).map((child) => convertNodeToLexical(child)),
  };
}
